'use client';

import React, {useEffect, useState} from 'react';
import {getMonolithApiHeaders} from '@udemy-v2/api-fetch';
import {AssetStatus, CaptionSource, MediaSourceType, AssetVideo} from '@/generated/graphql';

interface ApiAssetData {
    media_license_token?: string;
    media_sources?: Array<{
        type: string;
        src: string;
        label?: string;
    }>;
    captions?: Array<{
        language: string;
        url: string;
        label: string;
    }>;
    status?: string;
    time_estimation?: number;
}

interface UseFetchAssetResult {
    assetVideo?: AssetVideo;
    isLoading: boolean;
    error?: string;
}

/**
 * Transforms raw asset data into AssetVideo format compatible with VideoPlayerProvider
 * @param assetData - The raw asset data from API
 * @param assetId - The asset ID
 * @param assetTitle - The asset title
 * @returns AssetVideo object or undefined if no asset data
 */
export function transformAssetToVideoAsset(
    assetData: ApiAssetData | undefined,
    assetId: number,
    assetTitle: string,
): AssetVideo | undefined {
    if (!assetData) return undefined;

    // Check if data has minimum required structure - if it's completely empty or malformed, return undefined
    const hasMinimumData =
        Object.keys(assetData).length > 0 &&
        (assetData.media_sources !== undefined ||
            assetData.media_license_token !== undefined ||
            assetData.time_estimation !== undefined ||
            assetData.status !== undefined);

    if (!hasMinimumData) return undefined;

    // Transform media sources to match AssetMediaSource type
    const mediaSources = (assetData.media_sources || []).map((source) => ({
        __typename: 'AssetMediaSource' as const,
        label: source.label || 'auto',
        mediaType:
            source.type === 'video/mp4'
                ? MediaSourceType.VideoMp4
                : MediaSourceType.ApplicationXMpegurl,
        src: source.src,
    }));

    // Transform captions to match AssetCaption type
    const captions = (assetData.captions || []).map((caption, index) => ({
        __typename: 'AssetCaption' as const,
        id: `caption-${index}`,
        fileName: `caption-${index}.vtt`,
        language: caption.language,
        source: CaptionSource.Auto,
        status: AssetStatus.Success,
        title: caption.label,
        url: caption.url,
        videoLabel: caption.label,
    }));

    return {
        __typename: 'AssetVideo' as const,
        id: assetId.toString(),
        mediaSources,
        captions,
        accessToken: assetData.media_license_token ?? null,
        downloads: [],
        status: AssetStatus.Success,
        processingErrors: null,
        duration: assetData.time_estimation || 0,
        isDrmProtected: false,
        thumbnailTrack: null,
        title: assetTitle,
    };
}

/**
 * Fetches asset data from the API
 * @param assetId - The asset ID to fetch
 * @returns Promise<ApiAssetData> The asset data
 * @throws Error if the API request fails
 */
export async function fetchAssetData(assetId: number): Promise<ApiAssetData> {
    const response = await fetch(
        `/api-2.0/assets/${assetId}/?fields[asset]=@min,status,delayed_asset_message,processing_errors,time_estimation,media_license_token,media_sources,thumbnail_url,captions,thumbnail_sprite,created&fields[caption]=@default,is_translation`,
        {
            method: 'GET',
            headers: {
                ...getMonolithApiHeaders(),
            },
        },
    );

    if (!response.ok) {
        throw new Error(`Failed to fetch asset data: ${response.status} ${response.statusText}`);
    }

    const data: ApiAssetData = await response.json();
    return data;
}

/**
 * Hook to fetch asset data and transform it for video player usage
 * @param assetId - The asset ID to fetch, or undefined to skip fetching
 * @param assetTitle - The title for the asset (used in transformation)
 * @returns Object containing assetVideo, isLoading, and error states
 */
export const useFetchAsset = (assetId?: number, assetTitle?: string): UseFetchAssetResult => {
    const [assetData, setAssetData] = useState<ApiAssetData | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (!assetId) {
            setAssetData(undefined);
            setIsLoading(false);
            setError(undefined);
            return;
        }

        const fetchData = async () => {
            setIsLoading(true);
            setError(undefined);

            try {
                const data = await fetchAssetData(assetId);
                setAssetData(data);
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to load asset';
                setError(errorMessage);
                console.error('Failed to fetch asset data:', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [assetId]);

    // Transform asset data to video asset format
    const assetVideo = React.useMemo(() => {
        if (!assetData || !assetId || !assetTitle) return undefined;
        return transformAssetToVideoAsset(assetData, assetId, assetTitle);
    }, [assetData, assetId, assetTitle]);

    return {
        assetVideo,
        isLoading,
        error,
    };
};
